/**
 * AI Service
 * Frontend wrappers for all AI/ML capabilities provided by the backend
 * 
 * DISCOVERED AI ENDPOINTS:
 * ========================
 * 
 * 1. SEMANTIC SEARCH ENDPOINTS (Base: /api/search)
 *    - POST /api/search/transactions
 *      Request: { query: string, limit?: number }
 *      Response: { success: true, data: { results: Array, totalSearched: number, query: string } }
 *    
 *    - POST /api/search/shopping-items
 *      Request: { query: string, limit?: number, listId?: string }
 *      Response: { success: true, data: { results: Array, totalSearched: number, query: string } }
 *    
 *    - POST /api/search/find-patterns
 *      Request: { startDate?: Date, endDate?: Date, minConfidence?: number }
 *      Response: { success: true, data: { patterns: Array } }
 * 
 * 2. ML CATEGORIZATION ENDPOINTS (Base: /api/search)
 *    - POST /api/search/suggest-category
 *      Request: { description: string, amount?: number, merchant?: string }
 *      Response: { success: true, data: { suggestion: { categoryId, categoryName, confidence, alternatives } } }
 * 
 * 3. BUDGET AI ENDPOINTS (Base: /api/search)
 *    - POST /api/search/budget-analysis
 *      Request: { budgetId?: string, startDate?: Date, endDate?: Date }
 *      Response: { success: true, data: { analysis: Object } }
 *    
 *    - POST /api/search/budget-recommendations
 *      Request: { budgets: Array, spending: Array, startDate?: Date, endDate?: Date }
 *      Response: { success: true, data: { recommendations: Array } }
 * 
 * 4. FINANCIAL INSIGHTS ENDPOINTS (Base: /api/search)
 *    - POST /api/search/spending-analysis
 *      Request: { startDate: Date, endDate: Date, categories?: Array }
 *      Response: { success: true, data: { insights: Object } }
 *    
 *    - POST /api/search/personalized-advice
 *      Request: { question: string, startDate?: Date, endDate?: Date }
 *      Response: { success: true, data: { advice: string } }
 * 
 * 5. AI INSIGHTS ENDPOINTS (Base: /api/insights)
 *    - GET /api/insights/dashboard?startDate=...&endDate=...
 *      Response: { success: true, data: { ai: { smartInsights, chartExplanations, nextBestActions } } }
 *    
 *    - POST /api/insights/ask
 *      Request: { question: string, startDate?: Date, endDate?: Date }
 *      Response: { success: true, answer: string }
 *    
 *    - POST /api/insights/enhance-description
 *      Request: { description: string, category: string, subcategory?: string, type?: string }
 *      Response: { success: true, data: { enhanced: string } }
 */

import { apiClient } from './api';

/**
 * Semantic Search Services
 */

/**
 * Search user's transactions using natural language
 * @param {string} query - Natural language search query
 * @param {number} [limit=10] - Maximum number of results
 * @returns {Promise<{results: Array, totalSearched: number, query: string}>}
 */
export const searchTransactions = async (query, limit = 10) => {
  if (!query || query.trim().length < 2) {
    throw new Error('Search query must be at least 2 characters');
  }

  const response = await apiClient.post('/search/transactions', {
    query: query.trim(),
    limit
  });

  return response.data;
};

  /**
   * Ask FinList a shopping-focused analytical question (Phase 2)
   * Heuristic determines when user intends an analytic query vs plain search.
   * Backend expected endpoint: POST /api/search/shopping-items (semantic) OR
   * dedicated analytical endpoint if available in future phases.
   * For Phase 2 we reuse /search/shopping-items for semantic enrichment when
   * question intent detected, and return structured summary locally.
   *
   * @param {string} query - User's natural language question.
   * @param {Object} options - Optional context.
   * @param {string} [options.listId] - Specific list scope.
   * @param {string} [options.timeRange] - e.g. 'month', 'week'. (Future use)
   * @returns {Promise<{summary: string, stats?: Object, results?: Array}>}
   */
  export const askShoppingAI = async (query, options = {}) => {
    if (!query || query.trim().length < 4) {
      throw new Error('Question must be at least 4 characters');
    }
    const { listId } = options;
    // Phase 2: leverage semantic search for contextual grounding
    const semantic = await searchShoppingItems(query, { limit: 8, listId });
    // PHASE5 telemetry
    console.log('[AI-REQUEST] shopping', { action: 'askShoppingAI', query: query.trim(), matched: semantic.results?.length || 0 });
    // Build lightweight summary (frontend side) until dedicated backend analytic endpoint exists
    const itemNames = semantic.results?.map(r => r.data?.name).filter(Boolean) || [];
    const summary = itemNames.length
      ? `Found ${semantic.results.length} related items: ${itemNames.slice(0,5).join(', ')}${itemNames.length>5?'…':''}`
      : 'No directly related items found. Try refining your question.';
    return {
      summary,
      results: semantic.results,
      stats: { totalMatched: semantic.results?.length || 0 }
    };
  };

/**
 * Search shopping items using natural language
 * @param {string} query - Natural language search query (e.g., "cheap vegetarian dinner")
 * @param {Object} options - Search options
 * @param {number} [options.limit=10] - Maximum number of results
 * @param {string} [options.listId] - Optional list ID to search within
 * @returns {Promise<{results: Array, totalSearched: number, query: string}>}
 */
export const searchShoppingItems = async (query, options = {}) => {
  if (!query || query.trim().length < 2) {
    throw new Error('Search query must be at least 2 characters');
  }

  const { limit = 10, listId } = options;

  const response = await apiClient.post('/search/shopping-items', {
    query: query.trim(),
    limit,
    ...(listId && { listId })
  });

  // Extract results array from response
  // Backend returns { success: true, data: { results: [...], totalSearched, query } }
  const rawResults = response.data?.results || response.results || [];
  
  // Flatten the results - backend returns { id, data: {...}, similarity }
  // We want { id, name, category, similarity, ... }
  const results = rawResults.map(item => ({
    id: item.id,
    name: item.data?.name || item.name || 'Unknown',
    notes: item.data?.notes || item.notes || '',
    category: item.data?.category || item.category || '',
    quantity: item.data?.quantity || item.quantity || 1,
    unitPrice: item.data?.unitPrice || item.unitPrice || 0,
    listId: item.data?.listId || item.listId,
    listName: item.data?.listName || item.listName,
    similarity: item.similarity || 0
  }));
  
  console.log('[searchShoppingItems] Response:', response.data, 'Extracted results:', results.length, 'First result:', results[0]);
  return results;
};

/**
 * Find spending patterns in user's transaction history
 * @param {Object} options - Pattern detection options
 * @param {Date} [options.startDate] - Start date for pattern search
 * @param {Date} [options.endDate] - End date for pattern search
 * @param {number} [options.minConfidence=0.7] - Minimum confidence threshold
 * @returns {Promise<{patterns: Array}>}
 */
export const findSpendingPatterns = async (options = {}) => {
  const { startDate, endDate, minConfidence = 0.7 } = options;

  const response = await apiClient.post('/search/find-patterns', {
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() }),
    minConfidence
  });

  return response.data;
};

/**
 * ML Categorization Services
 */

/**
 * Get AI-suggested category for a transaction
 * @param {Object} transaction - Transaction details
 * @param {string} transaction.description - Transaction description
 * @param {number} [transaction.amount] - Transaction amount
 * @param {string} [transaction.merchant] - Merchant name
 * @returns {Promise<{suggestion: {categoryId: string, categoryName: string, confidence: number, alternatives: Array}}>}
 */
export const suggestCategory = async (transaction) => {
  if (!transaction.description || transaction.description.trim().length === 0) {
    throw new Error('Transaction description is required');
  }

  const response = await apiClient.post('/search/suggest-category', {
    description: transaction.description.trim(),
    ...(transaction.amount && { amount: transaction.amount }),
    ...(transaction.merchant && { merchant: transaction.merchant })
  });

  return response.data;
};

/**
 * Get AI explanation for why a category was chosen
 * Phase 1: Smart Transaction Engine 2.0
 * 
 * @param {Object} params - Explanation parameters
 * @param {string} params.description - Transaction description
 * @param {string} params.categoryId - The chosen category ID
 * @param {number} params.confidence - Confidence score (0-1)
 * @param {number} [params.amount] - Transaction amount
 * @param {string} [params.merchant] - Merchant name
 * @returns {Promise<Object>} Explanation with reasoning and alternatives
 */
export const explainCategory = async (params) => {
  const { description, categoryId, confidence, amount, merchant } = params;
  
  if (!description || !categoryId) {
    throw new Error('Description and categoryId are required');
  }

  const response = await apiClient.post('/search/explain-category', {
    description: description.trim(),
    categoryId,
    confidence: confidence || 0.8,
    ...(amount && { amount }),
    ...(merchant && { merchant })
  });

  return response.data;
};

/**
 * Get transaction behaviour insights
 * Phase 2: Smart Transaction Engine 2.0
 * 
 * @param {Object} options - Insights options
 * @param {string} [options.period='month'] - Analysis period ('week' or 'month')
 * @returns {Promise<Object>} Transaction insights including velocity, subscriptions, trends
 */
export const getTransactionInsights = async (options = {}) => {
  const { period = 'month' } = options;

  const response = await apiClient.post('/search/transaction-insights', {
    period
  });

  // Return the inner data object for direct use in components
  return response.data?.data || response.data;
};

/**
 * Get smart transaction tasks/alerts
 * Phase 3: Smart Transaction Engine 2.0
 * 
 * Detects refund verifications, duplicate charges, habit leaks, unusual amounts
 * 
 * @returns {Promise<Object>} Transaction tasks and summary
 */
export const getTransactionTasks = async () => {
  const response = await apiClient.post('/search/transaction-tasks', {});
  // Return the inner data object for direct use in components
  return response.data?.data || response.data;
};

/**
 * Get transaction gamification status
 * Phase 4: Smart Transaction Engine 2.0
 * 
 * Returns streaks, XP, level, badges, and clean ledger status
 * 
 * @returns {Promise<Object>} Gamification status including xp, level, badges, streak
 */
export const getTransactionGamification = async () => {
  const response = await apiClient.post('/search/transaction-gamification', {});
  // Return the inner data object for direct use in components
  return response.data?.data || response.data;
};

/**
 * Budget AI Services
 */

/**
 * Analyze a specific budget or spending category
 * @param {Object} options - Analysis options
 * @param {string} [options.budgetId] - Specific budget ID to analyze
 * @param {Date} [options.startDate] - Start date for analysis
 * @param {Date} [options.endDate] - End date for analysis
 * @returns {Promise<{analysis: Object}>}
 */
export const analyzeBudget = async (options = {}) => {
  const { budgetId, startDate, endDate } = options;

  const response = await apiClient.post('/search/budget-analysis', {
    ...(budgetId && { budgetId }),
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() })
  });

  return response.data;
};

/**
 * Get AI recommendations for budget adjustments
 * @param {Object} context - Budget context
 * @param {Array} context.budgets - Current budget allocations
 * @param {Array} context.spending - Historical spending data
 * @param {Date} [context.startDate] - Start date for recommendations
 * @param {Date} [context.endDate] - End date for recommendations
 * @returns {Promise<{recommendations: Array}>}
 */
export const getBudgetRecommendations = async (context) => {
  const { budgets, spending, startDate, endDate } = context;

  const response = await apiClient.post('/search/budget-recommendations', {
    budgets,
    spending,
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() })
  });

  return response.data;
};

/**
 * Financial Insights Services
 */

/**
 * Get AI-generated spending analysis for a time period
 * @param {Object} options - Analysis options
 * @param {Date} options.startDate - Start date for analysis
 * @param {Date} options.endDate - End date for analysis
 * @param {Array} [options.categories] - Optional specific categories to analyze
 * @returns {Promise<{insights: Object}>}
 */
export const getSpendingAnalysis = async (options) => {
  const { startDate, endDate, categories } = options;

  if (!startDate || !endDate) {
    throw new Error('Start date and end date are required');
  }

  const response = await apiClient.post('/search/spending-analysis', {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(categories && { categories })
  });

  return response.data;
};

/**
 * Ask AI a personalized financial question
 * @param {string} question - User's financial question
 * @param {Object} [options] - Question context options
 * @param {Date} [options.startDate] - Start date for context
 * @param {Date} [options.endDate] - End date for context
 * @returns {Promise<{advice: string}>}
 */
export const askPersonalizedAdvice = async (question, options = {}) => {
  if (!question || question.trim().length === 0) {
    throw new Error('Question is required');
  }

  const { startDate, endDate } = options;

  const response = await apiClient.post('/search/personalized-advice', {
    question: question.trim(),
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() })
  });

  return response.data;
};

/**
 * AI Insights Services (from insights API)
 */

/**
 * Get comprehensive dashboard insights with AI
 * @param {Date} [startDate] - Start date for insights
 * @param {Date} [endDate] - End date for insights
 * @returns {Promise<Object>} Dashboard data with AI insights
 */
export const getDashboardInsights = async (startDate, endDate) => {
  const response = await apiClient.get('/insights/dashboard', {
    params: {
      ...(startDate && { startDate: startDate.toISOString() }),
      ...(endDate && { endDate: endDate.toISOString() })
    }
  });

  return response.data;
};

/**
 * Ask AI a question about financial insights
 * (Note: This is different from askPersonalizedAdvice - uses insights API)
 * @param {string} question - Question about insights
 * @param {Date} [startDate] - Start date for context
 * @param {Date} [endDate] - End date for context
 * @returns {Promise<string>} AI answer
 */
export const askInsightsQuestion = async (question, startDate, endDate) => {
  if (!question || question.trim().length === 0) {
    throw new Error('Question is required');
  }

  const response = await apiClient.post('/insights/ask', {
    question: question.trim(),
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() })
  });

  return response.answer; // Note: This endpoint returns { answer } directly
};

/**
 * Enhance a description using AI
 * @param {Object} options - Enhancement options
 * @param {string} options.description - Original description
 * @param {string} options.category - Item/transaction category
 * @param {string} [options.subcategory] - Optional subcategory
 * @param {string} [options.type='item'] - Type (e.g., 'item', 'transaction')
 * @returns {Promise<string>} Enhanced description
 */
export const enhanceDescription = async (options) => {
  const { description, category, subcategory, type = 'item' } = options;

  if (!description || !category) {
    throw new Error('Description and category are required');
  }

  const response = await apiClient.post('/insights/enhance-description', {
    description: description.trim(),
    category,
    ...(subcategory && { subcategory }),
    type
  });

  return response.data.enhanced;
};

/**
 * PFM Snapshot Services
 */

/**
 * Get unified financial snapshot from PFM Engine
 * @param {Object} options - Snapshot options
 * @param {'month'|'week'|'year'|'custom'} [options.period='month'] - Period type
 * @param {number} [options.year] - Year for period
 * @param {number} [options.month] - Month for period (1-indexed)
 * @param {Date|string} [options.startDate] - Custom start date
 * @param {Date|string} [options.endDate] - Custom end date
 * @returns {Promise<{snapshot: Object}>}
 */
export const getFinancialSnapshot = async (options = {}) => {
  const { period = 'month', year, month, startDate, endDate } = options;

  const params = new URLSearchParams();
  params.append('period', period);
  if (year) params.append('year', year.toString());
  if (month) params.append('month', month.toString());
  if (startDate) params.append('startDate', startDate instanceof Date ? startDate.toISOString() : startDate);
  if (endDate) params.append('endDate', endDate instanceof Date ? endDate.toISOString() : endDate);

  // apiClient.get already unwraps response.data, so return directly
  return apiClient.get(`/insights/financial-snapshot?${params.toString()}`);
};

/**
 * AI Item Suggestion Services
 */

/**
 * Get AI-suggested items for a category
 * @param {Object} options - Suggestion options
 * @param {string} options.category - Category ID (e.g., 'cat_groceries', 'utilities')
 * @param {string} [options.categoryName] - Category display name
 * @param {'expense'|'income'} [options.type='expense'] - Transaction type
 * @param {number} [options.limit=8] - Max number of suggestions
 * @returns {Promise<{suggestions: Array<{name: string, note: string}>}>}
 */
export const suggestItems = async (options) => {
  const { category, categoryName, type = 'expense', limit = 8 } = options;

  if (!category && !categoryName) {
    throw new Error('Category or categoryName is required');
  }

  const response = await apiClient.post('/insights/suggest-items', {
    category,
    categoryName,
    type,
    limit
  });

  return response.data;
};

/**
 * Generate AI notes for an item based on category context
 * @param {Object} options - Note generation options
 * @param {string} options.itemName - The item name
 * @param {string} options.category - Category ID or name
 * @param {'expense'|'income'} [options.type='expense'] - Transaction type
 * @returns {Promise<{note: string, confidence: number}>}
 */
export const generateNotes = async (options) => {
  const { itemName, category, type = 'expense' } = options;

  if (!itemName) {
    throw new Error('itemName is required');
  }

  const response = await apiClient.post('/insights/generate-notes', {
    itemName,
    category,
    type
  });

  return response.data;
};

/**
 * Default export with all AI services
 */
const aiService = {
  // Semantic Search
  searchTransactions,
  searchShoppingItems,
  findSpendingPatterns,
  
  // ML Categorization
  suggestCategory,
  
  // Budget AI
  analyzeBudget,
  getBudgetRecommendations,
  
  // Financial Insights
  getSpendingAnalysis,
  askPersonalizedAdvice,
  
  // AI Insights
  getDashboardInsights,
  askInsightsQuestion,
  enhanceDescription,
  
  // PFM Snapshot
  getFinancialSnapshot,
  
  // AI Item Suggestions
  suggestItems,
  generateNotes
};

export default aiService;
